import React, { useEffect, useState } from "react";
import { Layers, Trash2, Users, Loader2 } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api, uploadUrl } from "@/lib/api";
import { useNav, VIEWS } from "@/lib/nav";
import { PackResultView } from "@/pages/ProposePage";
import { CommentsPanel } from "@/components/CommentsPanel";

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : "");

/** Saved model checks (and older rule packs), listed under the model's name. */
export function ModelCheckList({ q }) {
  const { navigate } = useNav();
  const [data, setData] = useState(null);
  const load = () => api.listPacks({ q, limit: 200 }).then(setData).catch((e) => console.warn("listPacks failed", e));
  useEffect(() => {
    const t = setTimeout(load, 200);
    const live = setInterval(load, 15000);  // workgroup members see each other's new checks
    return () => { clearTimeout(t); clearInterval(live); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  const onDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this model check from history?")) return;
    await api.deletePack(id);
    load();
  };
  if (!data) return <p className="text-sm text-muted-foreground"><Loader2 className="w-4 h-4 inline animate-spin" /> Loading model checks…</p>;
  if (!data.items.length) return <p className="text-sm text-muted-foreground italic" data-testid="model-check-empty">No model checks saved yet.</p>;
  return (
    <div className="grid gap-2" data-testid="model-check-list">
      {data.items.map((p) => (
        <Card key={p.id} className="cursor-pointer hover:border-primary/60 transition-colors" onClick={() => navigate(VIEWS.modelCheckDetail(p.id))} data-testid={`model-check-item-${p.id}`}>
          <CardContent className="p-3 flex items-center gap-3">
            {p.card?.image_path ? (
              <img src={uploadUrl(p.card.image_paths?.[0] || p.card.image_path)} alt="" className="w-12 h-12 object-cover rounded border border-border" />
            ) : (
              <div className="w-12 h-12 rounded border border-border grid place-items-center text-cyan-400"><Users className="w-5 h-5" /></div>
            )}
            <div className="min-w-0 flex-1">
              <p className="font-semibold truncate">{p.card?.name || p.name || "Untitled check"}</p>
              <p className="text-xs text-muted-foreground truncate">
                {(p.rule_names || []).join(", ")} · {p.interaction_count || 0} interaction{p.interaction_count === 1 ? "" : "s"} · {p.created_by?.name ? `${p.created_by.name} · ` : ""}{fmtDate(p.created_at)}
              </p>
            </div>
            {!p.card && <Badge variant="outline" className="text-[10px]">rule pack</Badge>}
            <Button size="icon" variant="ghost" onClick={(e) => onDelete(p.id, e)} data-testid={`model-check-delete-${p.id}`}><Trash2 className="w-4 h-4" /></Button>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

export function ModelCheckDetail({ id }) {
  const [pack, setPack] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.getPack(id).then(setPack).catch((e) => setError(e?.response?.data?.detail || e.message));
  }, [id]);
  if (error) return <p className="text-rose-300 text-sm">{error}</p>;
  if (!pack) return <p className="text-sm text-muted-foreground"><Loader2 className="w-4 h-4 inline animate-spin" /> Loading…</p>;
  const card = pack.card;
  return (
    <div className="grid gap-4" data-testid="model-check-detail">
      <Card>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Layers className="w-5 h-5 text-primary" />
            <h2 className="text-xl font-semibold" data-testid="model-check-detail-title">{card?.name || pack.name || "Model check"}</h2>
            <span className="text-xs text-muted-foreground">{pack.created_by?.name ? `by ${pack.created_by.name} · ` : ""}{fmtDate(pack.created_at)}</span>
            {(pack.tags || []).map((t) => <Badge key={t} variant="outline" className="text-[10px]">{t}</Badge>)}
          </div>
          {pack.intent && <p className="text-sm"><span className="text-muted-foreground">Intent:</span> {pack.intent}</p>}
          {card && (
            <div className="space-y-3">
              {(card.image_paths || (card.image_path ? [card.image_path] : [])).length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {(card.image_paths || [card.image_path]).map((p, i) => (
                    <a key={p} href={uploadUrl(p)} target="_blank" rel="noreferrer">
                      <img src={uploadUrl(p)} alt={`Card screenshot ${i + 1}`} className="h-48 rounded border border-border" data-testid={i === 0 ? "model-check-detail-image" : `model-check-detail-image-${i}`} />
                    </a>
                  ))}
                </div>
              )}
              {(card.rules || []).some((r) => r.is_new) && (
                <p className="text-sm" data-testid="model-check-detail-new-rules">
                  <span className="text-muted-foreground">New / changed on this card:</span>{" "}
                  {card.rules.filter((r) => r.is_new).map((r) => r.name).join(", ")}
                </p>
              )}
              <details className="rounded border border-border bg-background/40 p-3">
                <summary className="text-sm cursor-pointer">Model card {card.placement ? `· ${card.placement}` : ""}</summary>
                <pre className="whitespace-pre-wrap font-mono text-xs mt-2">{card.text}</pre>
              </details>
            </div>
          )}
        </CardContent>
      </Card>
      <PackResultView packResult={pack} savedPackId={pack.id} ruleType={pack.rule_type} modelTypes={pack.model_types || []} />
      <CommentsPanel targetType="pack" targetId={pack.id} />
    </div>
  );
}
